import { Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getDateFormat } from "../../../utils/date-format";
import { usePermissions } from "../../../hooks/usePermissions";
import CaseReportsAPI from "../../../api/cases/reports";
import ToolBarItems from "../../../components/ToolBarItems.js";
import { DownloadIcon, Trash2Icon } from "lucide-react";
import { Button, useTheme } from "@monolith-forensics/monolith-ui";
import { CaseReportExportData } from "./types/CaseReport";

const DefaultQueryKey = "cases:reports:exports";

const ActionCell = ({ rowData }: { rowData: CaseReportExportData }) => {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [showDeleteModel, setShowDeleteModel] = useState(false);

  const handleSubmit = () => {
    queryClient.refetchQueries({
      queryKey: [DefaultQueryKey],
      type: "active",
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 10,
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        width: "100%",
        userSelect: "none",
      }}
    >
      <DeleteModal
        open={showDeleteModel}
        handleClose={() => setShowDeleteModel(false)}
        onSubmit={() => handleSubmit()}
        defaultInfo={rowData}
      />
      {rowData.is_ready && (
        <Button
          size="xxs"
          variant="text"
          color="primary"
          href={rowData.link}
          download={rowData.report_name}
          leftSection={<DownloadIcon size={12} />}
        >
          Download
        </Button>
      )}
      <Button
        size="xxs"
        variant="text"
        color="error"
        disabled={!hasPermission()}
        leftSection={<Trash2Icon size={12} />}
        onClick={() => {
          setShowDeleteModel(true);
        }}
      >
        Delete
      </Button>
    </div>
  );
};

const DeleteModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}: {
  open: boolean;
  handleClose?: () => void;
  onSubmit?: () => void;
  defaultInfo: CaseReportExportData;
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = () => {
    CaseReportsAPI.deleteCaseReportExport({
      case_id: defaultInfo.case_id,
      case_report_id: defaultInfo.case_report_id,
      report_id: defaultInfo.report_id,
    }).then(() => {
      enqueueSnackbar(`Report Export Deleted.`, {
        variant: "success",
      });
      onSubmit();
    });
    handleClose();
  };

  const handleCancel = () => handleClose();

  return (
    <div>
      <Modal
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") handleClose();
        }}
        style={{ zIndex: 1400 }}
      >
        <div
          style={{
            marginTop: 20,
            marginBottom: 20,
            width: 400,
            backgroundColor: theme.palette.background.default,
            position: "absolute",
            left: "50%",
            top: "20%",
            transform: "translate(-35%, -20%)",
            padding: 20,
            outline: "none",
          }}
        >
          <div style={{ marginBottom: 15, fontSize: "large" }}>
            Delete Report Export
          </div>
          <div style={{ margin: "10px 0px" }}>
            Are you sure you want to delete this Report Export?
          </div>
          <div style={{ margin: "10px 0px" }}>"{defaultInfo.report_name}"</div>
          <div
            style={{
              marginTop: 30,
            }}
          >
            <ToolBarItems
              submitText="Delete Report Export"
              submitColor="error"
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const ReportDetailsColumnDefs = [
  {
    dataField: "report_name",
    caption: "Name",
    titleRender: (rowData: CaseReportExportData) => rowData.report_name,
  },
  {
    dataField: "created_on",
    caption: "Created On",
    dataType: "date",
    format: {
      type: getDateFormat({ isMoment: true, includeTime: true }),
    },
  },
  {
    dataField: "created_by",
    caption: "Created By",
  },
  {
    dataField: "status",
    caption: "Status",
  },
  {
    dataField: "is_shared",
    caption: "",
    render: (rowData: CaseReportExportData) => <ActionCell rowData={rowData} />,
  },
];

export default ReportDetailsColumnDefs.slice();
